/**
 * Sheet „Sollstellung korrigieren" (`charge-sheet`, Spec 04a, „Reiter Geld
 * eines Vertrags"). Öffnet beim Klick auf eine Zeile in `ChargesList`.
 *
 * **Kein Netto-Vorschlag — wie iOS
 * (`ChargeCorrectionViewModel.swift`).** `ContractCharge` liefert nur den
 * fertigen Bruttobetrag (`charged_cents`/`charged_display`), keinen
 * separaten Netto-Wert. Bei einer besteuerten Sollstellung (`tax_rate_bp !=
 * 0`, z. B. `app/seed.py:364`) wäre der Bruttobetrag als Netto-Vorbelegung
 * schlicht falsch — ein Speichern ohne jede Änderung hätte die Forderung
 * stillschweigend um den Steuersatz erhöht (KB1, gemessen: 19 % → 27.132
 * Cent). Das Feld startet deshalb leer; der aktuelle Bruttobetrag steht als
 * reiner Hinweistext daneben.
 */

import { useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { parseCents } from '@/lib/money'
import type { ContractCharge } from '@/features/billing/api'
import { useUpdateCharge } from '@/features/billing/api'
import { FormField, FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface ChargeSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** `null`, solange kein Sheet offen ist — vermeidet einen eigenen
   * `charge-sheet`-Ladezustand: Die Zeile trägt die Daten bereits. */
  readonly charge: ContractCharge | null
}

const EMPTY_FORM = { amount: '', note: '' }
type FormValues = typeof EMPTY_FORM

export function ChargeSheet({ open, onOpenChange, charge }: ChargeSheetProps) {
  const updateMutation = useUpdateCharge()

  const [form, setForm] = useState<FormValues>(EMPTY_FORM)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen mit einer neuen Sollstellung beginnt frisch — dasselbe
  // Muster wie `UnitSheet`/`MoveSheet` (React „Adjusting state when a prop
  // changes", während des Renderns statt in einem Effekt).
  const [syncedId, setSyncedId] = useState(charge?.charge_id)
  if (charge?.charge_id !== syncedId) {
    setSyncedId(charge?.charge_id)
    setForm(EMPTY_FORM)
    setFieldError(undefined)
    setGeneralError(undefined)
  }

  const parsedAmount = parseCents(form.amount)
  const isValid = parsedAmount !== null && parsedAmount > 0

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (charge === null || parsedAmount === null || !isValid) return
    setFieldError(undefined)
    setGeneralError(undefined)

    // `note` fehlt im Rumpf ganz, statt `undefined` zu tragen, wenn das Feld
    // leer bleibt — der Service unterscheidet „nicht gesendet" (vorhandene
    // Notiz bleibt) von „ausdrücklich auf null gesetzt" (Notiz gelöscht,
    // siehe Bericht 05-backend-dev-2, G-W3). Ein leeres Feld heißt hier „ich
    // ändere die Notiz nicht", nicht „lösche sie".
    const noteTrimmed = form.note.trim()
    try {
      await updateMutation.mutateAsync({
        chargeId: charge.charge_id,
        ifMatch: String(charge.version),
        body: { amount_net_cents: parsedAmount, ...(noteTrimmed !== '' ? { note: noteTrimmed } : {}) },
      })
      onOpenChange(false)
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form data-testid="charge-sheet" onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              Sollstellung korrigieren
            </SheetTitle>
            {charge !== null && (
              <p className="text-label text-muted">
                {charge.period_display} · {charge.kind.label}
              </p>
            )}
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <p className="text-label text-muted">Ein geänderter Betrag wird als Handkorrektur festgehalten.</p>
            {charge !== null && (
              // Kein Netto-Vorschlag — siehe Kommentar am Dateikopf.
              <p data-testid="charge-edit-current-gross" className="text-label text-muted">
                Aktuell berechnet: {charge.charged_display} brutto
              </p>
            )}
            <FormField
              label="Betrag (netto, in Euro)"
              testId="charge-edit-amount"
              apiField="amount_net_cents"
              errorPrefix="charge-edit"
              inputMode="decimal"
              required
              value={form.amount}
              onChange={(value) => {
                setForm((current) => ({ ...current, amount: value }))
              }}
              error={fieldError}
            />
            <FormField
              label="Notiz"
              testId="charge-edit-note"
              apiField="note"
              errorPrefix="charge-edit"
              value={form.note}
              onChange={(value) => {
                setForm((current) => ({ ...current, note: value }))
              }}
              error={fieldError}
            />
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="charge-edit-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="charge-edit-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="charge-edit-save"
                disabled={!isValid || updateMutation.isPending}
                aria-busy={updateMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Betrag ändern
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
